import { z } from 'zod';
import { Fact, SourceRef } from './ask.js';
import { ChatAction, ThreadSubject } from './chat-action.js';
/**
 * A single turn in a conversational refinement thread.
 *
 * Turns are stored inline on the parent {@link Thread} document for v0.2 —
 * one Firestore read fetches the entire conversation. Cap turn count to ~30
 * to stay well below Firestore's 1 MiB document limit; if conversations grow
 * past that, migrate to a `/threads/{id}/turns` sub-collection.
 */
export const ThreadTurn = z.object({
    id: z.string(),
    role: z.enum(['user', 'assistant']),
    content: z.string(),
    entities: z.array(z.string()).optional(),
    facts: z.array(Fact).optional(),
    sources: z.array(SourceRef).optional(),
    /**
     * Stage-2 actions the assistant requested during this turn. Persisted so
     * a thread reload shows the same "+ pinned Trita Parsi" chips that
     * appeared the first time. Optional for back-compat with v0.2 turns.
     */
    actions: z.array(ChatAction).optional(),
    createdAt: z.string(),
});
/**
 * A persisted conversational thread (frontend spec §5.7).
 *
 * Threads belong to a single analyst (`ownerUid`), can be pinned to the top
 * of the sidebar, renamed, and forked (forks reference the original via
 * `parentThreadId` and copy turns up to a chosen point).
 *
 * `subject` anchors the thread to a surface — an entity dossier, a graph
 * view, or `'free'` for command-center. Surface-anchored threads filter
 * the sidebar so a chat opened on /entity/Trita%20Parsi only shows past
 * Parsi conversations. Optional for back-compat with v0.2 threads created
 * before subjects existed; absent → treat as `{ kind: 'free' }`.
 */
export const Thread = z.object({
    id: z.string(),
    orgId: z.string(),
    title: z.string(),
    ownerUid: z.string(),
    pinned: z.boolean().default(false),
    subject: ThreadSubject.optional(),
    parentThreadId: z.string().optional(),
    turns: z.array(ThreadTurn).default([]),
    createdAt: z.string(),
    updatedAt: z.string(),
});
